import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowUp, FaBars, FaTimes } from "react-icons/fa";
import AmbientCanvas from "../components/landing/AmbientCanvas";
import FeatureShowcase from "../components/landing/FeatureShowcase";
import BrandLogo from "../components/BrandLogo";
import "./LandingPage.css";

const NAV_ITEMS = [
  { id: "home", label: "Home" },
  { id: "services", label: "Services" },
  { id: "features", label: "Features" },
  { id: "how-it-works", label: "How it works" },
  { id: "about", label: "About" },
];

const SERVICES = [
  {
    tag: "VFS",
    title: "VFS Booking",
    text: "Prepare applicant details, choose routes and appointment categories, then send VFS jobs from one place.",
  },
  {
    tag: "TLS",
    title: "TLS Form",
    text: "Select TLS country and centre, check bridge status, and start the TLS automation flow cleanly.",
  },
  {
    tag: "Ops",
    title: "Assisted Bookings",
    text: "Keep team-assisted cases organized with clear status tracking across assign and booked states.",
  },
];

const FEATURES = [
  {
    title: "Available slots",
    text: "Review dates found by the extension after calendar scans, without leaving the portal.",
    caption: "Live calendar results",
  },
  {
    title: "Team access",
    text: "Manage roles, permissions, agents, and clients with controlled access to each area.",
    caption: "Roles & permissions",
  },
  {
    title: "Extension license",
    text: "Connect a licensed Chrome extension so portal jobs and browser automation stay linked.",
    caption: "Bridge linked to portal",
  },
  {
    title: "Booking status",
    text: "Follow progress across assisted bookings and keep outcomes easy to monitor.",
    caption: "End-to-end job tracking",
  },
];

const FEATURE_AUTO_MS = 4500;

const STEPS = [
  {
    title: "Create your account",
    text: "Sign up or log in to open your Visa Portal workspace.",
  },
  {
    title: "Connect the extension",
    text: "Use your license key to verify the VFS Bridge extension.",
  },
  {
    title: "Choose VFS or TLS",
    text: "Open the service you need and fill the required booking details.",
  },
  {
    title: "Track the result",
    text: "Monitor jobs, slots, and booking status from the same platform.",
  },
];

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(media.matches);
    sync();
    media.addEventListener?.("change", sync);
    return () => media.removeEventListener?.("change", sync);
  }, []);

  return reduced;
}

function useRevealOnScroll() {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll(".lp-reveal"));
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);
}

export default function LandingPage() {
  const [activeSection, setActiveSection] = useState("home");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [featuresPaused, setFeaturesPaused] = useState(false);
  const [featureCycleToken, setFeatureCycleToken] = useState(0);
  const reducedMotion = useReducedMotion();
  useRevealOnScroll();

  const sectionIds = useMemo(() => NAV_ITEMS.map((item) => item.id), []);
  const activeFeatureData = FEATURES[activeFeature] || FEATURES[0];

  const selectFeature = (index) => {
    setActiveFeature(index);
    setFeatureCycleToken((token) => token + 1);
  };

  useEffect(() => {
    const onScroll = () => {
      const offset = window.scrollY + 120;
      let current = sectionIds[0];
      sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= offset) current = id;
      });
      setActiveSection(current);
      setShowScrollTop(window.scrollY > 420);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sectionIds]);

  useEffect(() => {
    if (reducedMotion || featuresPaused) return undefined;
    const timer = window.setInterval(() => {
      setActiveFeature((index) => (index + 1) % FEATURES.length);
    }, FEATURE_AUTO_MS);
    return () => window.clearInterval(timer);
  }, [reducedMotion, featuresPaused, featureCycleToken]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    setMobileOpen(false);
    el.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
  };

  return (
    <div className="lp">
      <header className="lp-nav">
        <button
          type="button"
          className="lp-brand"
          onClick={() => scrollToSection("home")}
          aria-label="Visa Appointment Bot home"
        >
          <BrandLogo asLink={false} height={44} />
        </button>

        <nav className="lp-nav-links" aria-label="Page sections">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`lp-nav-link${activeSection === item.id ? " is-active" : ""}`}
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="lp-nav-actions">
          <Link to="/login" className="lp-btn lp-btn-ghost">
            Login
          </Link>
          <Link to="/register" className="lp-btn lp-btn-dark">
            Sign up
          </Link>
        </div>

        <button
          type="button"
          className="lp-menu-btn"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileOpen((value) => !value)}
        >
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>
      </header>
      <div className="lp-nav-spacer" aria-hidden="true" />

      {mobileOpen ? (
        <div className="lp-mobile-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`lp-nav-link${activeSection === item.id ? " is-active" : ""}`}
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
          <Link to="/login" className="lp-btn lp-btn-ghost" onClick={() => setMobileOpen(false)}>
            Login
          </Link>
          <Link to="/register" className="lp-btn lp-btn-dark" onClick={() => setMobileOpen(false)}>
            Sign up
          </Link>
        </div>
      ) : null}

      {/* Home */}
      <section className="lp-hero" id="home">
        <div className="lp-hero-media" aria-hidden="true">
          {!reducedMotion ? (
            <video
              className="lp-hero-video"
              autoPlay
              muted
              loop
              playsInline
              poster="/assets/hero/hero-bg.jpg"
            >
              <source src="/assets/hero/hero-bg.mp4" type="video/mp4" />
            </video>
          ) : null}
          <picture className="lp-hero-image">
            <source srcSet="/assets/hero/hero-bg.webp" type="image/webp" />
            <img src="/assets/hero/hero-bg.jpg" alt="" draggable={false} />
          </picture>
        </div>
        {!reducedMotion ? <AmbientCanvas variant="hero" className="lp-canvas-hero" /> : null}
        <div className="lp-hero-veil" aria-hidden="true" />
        <div className="lp-hero-content lp-reveal is-in">
          <h1 className="lp-hero-title">Visa Portal</h1>
          <p className="lp-hero-text">
            A simple platform to manage VFS and TLS booking workflows, team access, and extension
            automation in one place.
          </p>
          <div className="lp-hero-actions">
            <Link to="/register" className="lp-btn lp-btn-orange">
              Get started
            </Link>
            <button type="button" className="lp-btn lp-btn-ghost" onClick={() => scrollToSection("services")}>
              View services
            </button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="lp-section" id="services">
        <div className="lp-section-inner">
          <div className="lp-section-head center lp-reveal">
            <p className="lp-eyebrow">Services</p>
            <h2 className="lp-h2">What you can run from the portal</h2>
            <p className="lp-lead">
              Clear service areas for the work your team already does — VFS bookings, TLS setup, and
              assisted operations.
            </p>
          </div>
          <div className="lp-services-grid">
            {SERVICES.map((service) => (
              <article className="lp-service lp-reveal" key={service.title}>
                <div className="lp-service-tag">{service.tag}</div>
                <h3>{service.title}</h3>
                <p>{service.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="lp-section lp-section-alt" id="features">
        <div className="lp-section-inner">
          <div
            className={`lp-features-layout${featuresPaused ? " is-paused" : ""}`}
            onMouseEnter={() => setFeaturesPaused(true)}
            onMouseLeave={() => {
              setFeaturesPaused(false);
              setFeatureCycleToken((token) => token + 1);
            }}
            onFocusCapture={() => setFeaturesPaused(true)}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setFeaturesPaused(false);
                setFeatureCycleToken((token) => token + 1);
              }
            }}
          >
            <div className="lp-features-copy lp-reveal">
              <div className="lp-section-head">
                <p className="lp-eyebrow">Features</p>
                <h2 className="lp-h2">Built for day-to-day booking work</h2>
                <p className="lp-lead">
                  Practical tools that support the full loop — from access control to slot review and
                  status tracking.
                </p>
              </div>
              <div className="lp-feature-list" role="tablist" aria-label="Portal features">
                {FEATURES.map((feature, index) => {
                  const isActive = index === activeFeature;
                  return (
                    <button
                      type="button"
                      role="tab"
                      id={`lp-feature-tab-${index}`}
                      aria-selected={isActive}
                      aria-controls="lp-feature-panel"
                      className={`lp-feature-item${isActive ? " is-active" : ""}`}
                      key={feature.title}
                      onClick={() => selectFeature(index)}
                    >
                      <span className="lp-feature-num" aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="lp-feature-body">
                        <h3>{feature.title}</h3>
                        <p>{feature.text}</p>
                      </div>
                      {isActive && !reducedMotion ? (
                        <span
                          key={`progress-${activeFeature}-${featureCycleToken}`}
                          className="lp-feature-progress"
                          aria-hidden="true"
                          style={{ animationDuration: `${FEATURE_AUTO_MS}ms` }}
                        />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className="lp-features-visual lp-reveal"
              id="lp-feature-panel"
              role="tabpanel"
              aria-labelledby={`lp-feature-tab-${activeFeature}`}
            >
              {reducedMotion ? (
                <div className="lp-features-fallback">
                  <span className="lp-features-caption-step">
                    {String(activeFeature + 1).padStart(2, "0")} / {String(FEATURES.length).padStart(2, "0")}
                  </span>
                  <strong>{activeFeatureData.title}</strong>
                  <span>{activeFeatureData.caption}</span>
                </div>
              ) : (
                <FeatureShowcase
                  activeIndex={activeFeature}
                  caption={{ title: activeFeatureData.title, text: activeFeatureData.caption }}
                  stepLabel={`${String(activeFeature + 1).padStart(2, "0")} / ${String(FEATURES.length).padStart(2, "0")}`}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="lp-section" id="how-it-works">
        <div className="lp-section-inner">
          <div className="lp-section-head center lp-reveal">
            <p className="lp-eyebrow">How it works</p>
            <h2 className="lp-h2">Four simple steps</h2>
            <p className="lp-lead">
              From account access to tracking results — a straightforward path anyone on the team can
              follow.
            </p>
          </div>
          <div className="lp-steps">
            {STEPS.map((step) => (
              <article className="lp-step lp-reveal" key={step.title}>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="lp-section lp-section-alt" id="about">
        <div className="lp-section-inner">
          <div className="lp-about-layout">
            <div className="lp-reveal">
              <p className="lp-eyebrow">About</p>
              <h2 className="lp-h2">Made for visa booking teams</h2>
              <p className="lp-lead">
                Visa Portal brings VFS and TLS workflows, people management, and extension connectivity
                into one consistent workspace — so operations stay organized and easier to run.
              </p>
              <ul className="lp-about-points">
                <li>One place for VFS and TLS booking lanes</li>
                <li>Roles and permissions for safer team access</li>
                <li>Extension bridge support for automation jobs</li>
              </ul>
            </div>
            <div className="lp-about-panel lp-reveal">
              <h3>Ready to use the portal?</h3>
              <p>
                Create an account to get started, or log in if you already have access to your team
                workspace.
              </p>
              <Link to="/register" className="lp-btn lp-btn-orange">
                Sign up
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="lp-cta lp-reveal">
        <div>
          <h2>Start with Visa Portal</h2>
          <p>Login or sign up to open services, features, and your booking workspace.</p>
        </div>
        <div className="lp-cta-actions">
          <Link to="/register" className="lp-btn lp-btn-orange">
            Sign up
          </Link>
          <Link to="/login" className="lp-btn lp-btn-ghost">
            Login
          </Link>
        </div>
      </section>

      <footer className="lp-footer">
        <span>© {new Date().getFullYear()} Visa Portal</span>
        <Link to="/privacy-policy">Privacy Policy</Link>
      </footer>

      <button
        type="button"
        className={`lp-scroll-top${showScrollTop ? " is-visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
        title="Back to top"
      >
        <FaArrowUp />
      </button>
    </div>
  );
}
